import React, { ReactNode } from "react";

import cx from "classnames";

import { Link, LinkProps } from "../Link";

import styles from "./Text.scss";
import { TextSizes } from "./types";

/**
 * A component that styles text with different options. This allows the
 * deduplication of many font settings throughout stylesheets with the simple
 * usage of this component.
 */
export function Text({
  size = "regular",
  children,
  className,
  color,
  paragraph,
  slim = false,
  ellipsisOverflow = false,
  maxChars,
  maxLines,
  textAlign = false,
  bold = false,
  wordBreak = false,
  preserveWhiteSpaces = false,
  linkProperties,
  ...elementProps
}: React.PropsWithChildren<{
  /**
   * Additional class names to apply to the text
   */
  className?: string;
  /**
   * Text color
   */
  color?: string | null;
  /**
   * Size of the text shown
   */
  size?: TextSizes;
  /**
   * If true, the text will be slim
   */
  slim?: boolean;
  /**
   * Sets the underlying semantic tag to a paragraph tag `<p>` and applies styles specific to paragraphs
   */
  paragraph?: boolean;
  /**
   * Use ellipsis to hide overflow
   */
  ellipsisOverflow?: boolean;
  /**
   * Maximum number of characters to display
   */
  maxChars?: number;
  /**
   * Ellipsis overflow applies on X line
   */
  maxLines?: 2 | 3 | 4 | 5;
  /**
   * Text alignment
   */
  textAlign?: "left" | "center" | "right" | "justify" | false;
  /**
   * Sets the font-weight as bold
   */
  bold?: boolean;
  /**
   * Break up a single long-word based on container width
   */
  wordBreak?: boolean;
  /**
   * Preserve newlines and whitespace in text content while respecting
   * line wraps (white-space: pre-wrap)
   */
  preserveWhiteSpaces?: boolean;
  /**
   * Properties to pass to Link component when rendering URLs as links.
   * If provided, URLs will be rendered as Link components.
   */
  linkProperties?: Partial<LinkProps>;
  /**
   * Additional element properties
   */
  [key: string]: any;
}>) {
  if (children === null || children === undefined || children === "") {
    return null;
  }

  const TagType = paragraph ? "p" : "span";

  let childrenToRender: ReactNode = children;
  if (typeof children === "string" && maxChars && children.length > maxChars) {
    childrenToRender = children.slice(0, maxChars).trim() + "...";
  }

  const addTitle =
    typeof childrenToRender === "string" &&
    maxChars &&
    (childrenToRender.length > maxChars || ellipsisOverflow)
      ? true
      : false;

  if (linkProperties && typeof childrenToRender === "string") {
    childrenToRender = convertUrlsToLinks(
      childrenToRender,
      linkProperties,
      size
    );
  }

  return (
    <TagType
      title={addTitle && typeof children === "string" ? children : undefined}
      {...(paragraph && { role: "paragraph" })}
      {...elementProps}
      className={cx(
        styles.text,
        {
          [styles["text--paragraph"]]: paragraph,
          [styles["text--slim"]]: slim,
          [styles["text--paragraph-large"]]: size === "paragraphLarge",
          [styles["text--paragraph-default"]]: size === "paragraph",
          [styles["text--paragraph-small"]]: size === "paragraphSmall",
          [styles["text--paragraph-xsmall"]]: size === "paragraphXsmall",
          [styles["text--extra-large"]]: size === "extraLarge",
          [styles["text--large"]]: size === "large",
          [styles["text--regular"]]: size === "regular",
          [styles["text--small"]]: size === "small",
          [styles["text--extra-small"]]: size === "extraSmall",
          [styles["text--timestamp"]]: size === "timestamp",
          [styles["text--subtitle-small"]]: size === "subtitleSmall",
          [styles["text--text-align-left"]]: textAlign === "left",
          [styles["text--text-align-center"]]: textAlign === "center",
          [styles["text--text-align-right"]]: textAlign === "right",
          [styles["text--text-align-justify"]]: textAlign === "justify",
          [styles["text--bold"]]: bold === true,
          [styles["text-break-word"]]: wordBreak === true,
          [styles["text--preserve-white-spaces"]]: preserveWhiteSpaces === true,
          [styles["ellipsis-overflow"]]: ellipsisOverflow && !maxLines,
          [styles["ellipsis-overflow-multiline"]]:
            ellipsisOverflow && Boolean(maxLines),
          [styles["trim--overflow--2"]]: maxLines === 2,
          [styles["trim--overflow--3"]]: maxLines === 3,
          [styles["trim--overflow--4"]]: maxLines === 4,
          [styles["trim--overflow--5"]]: maxLines === 5,
        },
        className
      )}
      style={{ color: color !== null ? color : undefined }}
    >
      {childrenToRender}
    </TagType>
  );
}

function convertUrlsToLinks(
  text: string,
  linkProperties: Partial<LinkProps>,
  textSize: TextSizes
): ReactNode {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const elements: ReactNode[] = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(urlRegex);

  // Create a new linkProps object that includes the default textSize
  // if one isn't already specified in linkProperties
  const linkProps = {
    ...linkProperties,
    textSize: linkProperties.textSize || textSize,
  };

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      elements.push(text.substring(lastIndex, match.index));
    }

    elements.push(
      <Link
        key={`link-${match.index}`}
        to={match[0]}
        target="_blank"
        rel="noopener noreferrer"
        {...linkProps}
      >
        {match[0]}
      </Link>
    );

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    elements.push(text.substring(lastIndex));
  }

  return <>{elements}</>;
}
